import React from 'react';
import type { ThemePalette } from '../../types';

export interface GraphNavEntry {
  graphId: string;
  graphLabel: string;
}

export interface GroupBreadcrumbProps {
  graphNavStack: GraphNavEntry[];
  /** Pop one level off the navigation stack (Tab equivalent). */
  exitGraph: () => void;
  palette: ThemePalette;
  /** Inline glassmorphic background style for the breadcrumb pill. */
  glass: React.CSSProperties;
}

/**
 * Blender-style breadcrumb shown when the user has entered one or more group/sub-graph
 * levels. The amber inset border highlights the active "you're inside a group" mode,
 * and each segment lets the user jump back to a specific ancestor (or all the way out).
 */
export const GroupBreadcrumb: React.FC<GroupBreadcrumbProps> = ({
  graphNavStack,
  exitGraph,
  palette,
  glass,
}) => {
  if (graphNavStack.length === 0) return null;
  return (
    <>
      {/* Amber border tint to indicate "you're inside a group" */}
      <div
        className="absolute inset-0 pointer-events-none z-[5]"
        style={{ boxShadow: 'inset 0 0 0 3px rgba(251,191,36,0.35)' }}
      />
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[100]">
        <div
          className="backdrop-blur-md border rounded-2xl px-4 py-2 shadow-2xl flex items-center gap-2"
          style={{ ...glass, borderColor: 'rgba(251,191,36,0.4)' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
          {/* Root = exit all levels */}
          <button
            onClick={() => {
              for (let s = 0; s < graphNavStack.length; s++) exitGraph();
            }}
            className="text-xs font-medium opacity-60 hover:opacity-100 transition-opacity"
            style={{ color: palette.panelSubText }}
          >
            Canvas
          </button>
          {graphNavStack.map((entry, i) => (
            <React.Fragment key={entry.graphId}>
              <span style={{ color: palette.panelBorder }} className="text-xs">›</span>
              <button
                onClick={() => {
                  // Click on ancestor: exit back to that level
                  const stepsBack = graphNavStack.length - 1 - i;
                  for (let s = 0; s < stepsBack; s++) exitGraph();
                }}
                className="text-xs font-semibold transition-opacity hover:opacity-80"
                style={{ color: i === graphNavStack.length - 1 ? '#fbbf24' : palette.panelSubText }}
              >
                {entry.graphLabel}
              </button>
            </React.Fragment>
          ))}
          <span style={{ color: palette.panelBorder }} className="text-xs mx-1">|</span>
          <button
            onClick={() => exitGraph()}
            className="text-xs font-medium border rounded-lg px-2 py-0.5 transition-colors hover:opacity-80"
            style={{ borderColor: 'rgba(251,191,36,0.4)', color: '#fbbf24' }}
          >
            Tab ↑ exit
          </button>
        </div>
      </div>
    </>
  );
};
